import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useSearch from 'hooks/useSearch';
import BarChart from 'components/ui/charts/BarChart';
import colorGenerator from 'utils/colorGenerator';

const PERIODS = [
  { code: 'DAILY', label: 'boss.payment.daily' },
  { code: 'MONTH', label: 'boss.payment.monthly' },
  { code: 'YEAR', label: 'boss.payment.yearly' },
];

const formatLabel = (date, period) => {
  if (period === 'YEAR') return String(date);
  if (period === 'MONTH') return moment(date, 'YYYY-MM').format('MM.YYYY');
  return moment(date, 'YYYY-MM-DD').format('DD.MM');
};

/**
 * Leadlar oqimi — manba (CrmField) kesimida stacked bar.
 * Kun / Oy / Yil ko'rinishlari (period) qo'llab-quvvatlanadi.
 */
const LeadsSourceWidgetChart = ({ info }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const query = useSearch();
  const { search } = useLocation();
  const [period, setPeriod] = useState('DAILY');

  const { data, isFetching } = useQuery({
    queryKey: ['/admin/widgets/chart2', 'LEADSSOURCE', info?.id, search, period],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/admin/widgets/chart2${search ? search + '&' : '?'}type=${info?.type}&id=${info?.id}&period=${period}`,
      );
      return response.data.data;
    },
    enabled: !!query.get('dashboard') && !!info,
    retry: false,
  });

  const { labels, datasets, sources, sourceTotals, total } = useMemo(() => {
    const rows = data?.data || [];
    const srcs = data?.sources || [];
    return {
      labels: rows.map((r) => formatLabel(r.date, period)),
      datasets: srcs.map((s) => ({
        label: s || '—',
        data: rows.map((r) => r.counts?.[s] || 0),
        backgroundColor: colorGenerator(),
        borderRadius: 4,
        stack: 'leads',
      })),
      sources: srcs,
      sourceTotals: data?.sourceTotals || {},
      total: data?.total || 0,
    };
  }, [data, period]);

  return (
    <div className='px-3 py-3 flex flex-col gap-3'>
      {/* Kun / Oy / Yil */}
      <div className='flex items-center justify-center gap-1'>
        {PERIODS.map((p) => (
          <button
            key={p.code}
            type='button'
            onClick={() => setPeriod(p.code)}
            className={`px-3 py-1 text-xs rounded-md border transition ${
              period === p.code
                ? 'bg-[#0057FF] text-white border-[#0057FF]'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t(p.label)}
          </button>
        ))}
      </div>

      {isFetching ? (
        <div className='h-[260px] flex items-center justify-center'>
          <CircularProgress size={26} />
        </div>
      ) : !datasets.length ? (
        <div className='text-center text-gray-400 py-10 text-sm'>
          <i className='bi bi-exclamation-octagon mr-1' />
          {t('common.global.noDataFound')}
        </div>
      ) : (
        <>
          <div className='h-[280px]'>
            <BarChart
              chartLabels={labels}
              chartDatasets={datasets}
              options={{
                scales: {
                  x: { stacked: true },
                  y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
                },
              }}
              optionPlugins={{
                legend: {
                  display: true,
                  position: 'bottom',
                  labels: { usePointStyle: true, pointStyle: 'rectRounded' },
                },
              }}
            />
          </div>

          <table className='w-full text-sm'>
            <tbody>
              {sources.map((s) => (
                <tr key={s} className='border-t border-gray-100'>
                  <td className='py-1.5'>{s || '—'}</td>
                  <td className='py-1.5 text-right font-medium'>
                    {sourceTotals[s] || 0}
                  </td>
                </tr>
              ))}
              <tr className='border-t-2 border-gray-200 font-semibold'>
                <td className='py-1.5'>{t('common.table.total')}</td>
                <td className='py-1.5 text-right'>{total}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default memo(LeadsSourceWidgetChart);
